import AccountTree from '@mui/icons-material/AccountTree';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import FileCopyOutlined from '@mui/icons-material/FileCopyOutlined';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import ReceiptOutlined from '@mui/icons-material/ReceiptOutlined';
import SendOutlined from '@mui/icons-material/SendOutlined';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import Sync from '@mui/icons-material/Sync';
import {
  checkboxClasses,
  formControlLabelClasses,
  inputAdornmentClasses,
  inputBaseClasses,
  svgIconClasses,
  tooltipClasses,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import { ButtonProps } from '@mui/material/Button';
import IconButton, { iconButtonClasses } from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography, { typographyClasses } from '@mui/material/Typography';
import {
  JobStateEvent,
  MessagingChannelType,
  ModelError_Response,
  OrderUnitOfMeasure,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { compact, isNil, snakeCase, startCase } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';

import Checkbox from '~components/Checkbox/Checkbox';
import GpsStatusPopover from '~components/Common/GpsStatusPopover';
import { CopyToClipboard } from '~components/CopyToClipboard/CopyToClipboard';
import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import NumberInput from '~components/FormFields/v2/NumberInput';
import RateAndTypeInput from '~components/FormFields/v2/RateAndTypeInput';
import TextInput from '~components/FormFields/v2/TextInput';
import TimeInput from '~components/FormFields/v2/TimeInput';
import JobPausedIndicator from '~components/Job/JobPausedIndicator';
import UomMismatchIndicator from '~components/Job/UomMismatchIndicator';
import LabelList from '~components/Labels/LabelList';
import { DONE_JOB_STATES } from '~components/Maps/v2/constants';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import StartConversationDialog from '~components/Messaging/StartConversationDialog';
import UnreadCountBadge from '~components/Messaging/UnreadCountBadge';
import {
  FlexColumn,
  OverflowAwareText,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import RateProvenanceTooltip, {
  PayCommissionBreakdown,
} from '~components/RateCard/RateProvenanceTooltip';
import { FeatureFlags } from '~constants/featureFlags';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import {
  isJobAssignmentCancelAction,
  trackJobAssignmentCancelled,
} from '~hooks/useAnalytics/trackJobAssignmentCancelled';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import useMaterials from '~hooks/useMaterials/useMaterials';
import { Order } from '~hooks/useOrders/models';
import useOrdersDispatch from '~hooks/useOrders/useOrdersDispatch';
import { GeofenceStartAdornment } from '~pages/Settings/Administration/Sites/components/GeofenceStartAdornment';
import { getFormattedRateValue } from '~pages/Settings/RatesManagement/rateUtils';
import useMessagingJobUnreadCountQuery from '~queries/useMessagingJobUnreadCountQuery';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';
import {
  getConsolidatedEquipmentName,
  getDriverInfoForCards,
} from '~utils/drivers/driver-utils';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import useShouldShowHaulerRateCard from '~utils/hooks/useShouldShowHaulerRateCard';
import {
  canMessageJobDriver,
  extractRateInfoFromJob,
  getLargestDeliveredQuantitySum,
  isJobDeletable,
  isJobSendable,
  jobPayCommission,
  resolveJobRateSides,
  resolveRateDisabledReasons,
  resolveRateEditableSides,
} from '~utils/jobs/job-utils';
import { extractWaypointData, userCompanyOwnsOrder } from '~utils/orders';
import { formatRateWithType, toRateNumber } from '~utils/rateFormatting';
import { jobHasAnyUomMismatch } from '~utils/uomMismatch';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import {
  formQuantityFieldRepresentsEmptyQuantity,
  getStringOrNumberFieldPositiveNumberValue,
  numberToIntegerOrDecimal,
} from '~utils/utilFunctions';

import { shouldEnableRecurringLoads } from '../../Sales/Orders/NewOrderForm/utils';
import { canAcceptJob, getJobNextEvents } from '../utils';
import { JobsTableColumnKey, jobsTableColumns } from './constants';
import {
  getJobCycleTimeText,
  getJobMaterialText,
  getJobStartDateText,
  getJobStartTimeText,
} from './dispatchExportColumns';
import EditJobRatesDialog from './EditJobRatesDialog';
import {
  EditJobTableRowsDto,
  InlineJobRateDto,
  inlineJobRateHasValueWithoutType,
  jobQuantitySchema,
  jobStartTimeSchema,
} from './editJobTableRowsSchema';
import EquipmentSelector from './EquipmentSelector';
import EtaCell from './EtaCell';
import { etaRowTintBgcolor } from './etaRowTint';
import JobAssigneeHandler from './JobAssigneeHandler';
import { Column, JobsTablePresentationContext } from './jobsTableStyledComponents';
import JobStatusTransitioner from './JobStatusTransitioner';
import RequestedEquipmentTypeSelector, {
  RequestedEquipmentTypeSelectorOption,
} from './RequestedEquipmentTypeSelector';

export const getJobsTableColumns = (
  isEditingJobs: boolean,
  showDispatchJobsTruckAndTrailerColumnSettingEnabled: boolean,
  showDispatchJobsMaterialsColumnSettingEnabled: boolean,
  showDispatchJobsPickupDropoffColumnSettingEnabled: boolean,
) => {
  const excludedKeys = isEditingJobs
    ? [
        JobsTableColumnKey.STATUS,
        JobsTableColumnKey.CYCLE_TIME,
        JobsTableColumnKey.REQUESTED,
        JobsTableColumnKey.DELIVERED,
        // GPS status is a read-only live indicator; in edit mode its 50px
        // column renders empty and just pushes the input columns apart.
        JobsTableColumnKey.GPS_STATUS,
      ]
    : [
        JobsTableColumnKey.RECURRING_LOADS,
        JobsTableColumnKey.LOADS_PER_TRUCK,
        JobsTableColumnKey.QUANTITY_PER_LOAD,
        JobsTableColumnKey.JOB_UNIT_OF_MEASURE,
        JobsTableColumnKey.JOB_QUANTITY,
      ];

  if (!showDispatchJobsTruckAndTrailerColumnSettingEnabled) {
    excludedKeys.push(JobsTableColumnKey.TRUCK_AND_TRAILER);
  }

  if (!showDispatchJobsMaterialsColumnSettingEnabled) {
    excludedKeys.push(JobsTableColumnKey.MATERIALS);
  }

  if (!showDispatchJobsPickupDropoffColumnSettingEnabled) {
    excludedKeys.push(JobsTableColumnKey.WAYPOINTS);
  }

  excludedKeys.push(JobsTableColumnKey.LOADS_PER_TRUCK);
  excludedKeys.push(JobsTableColumnKey.QUANTITY_PER_LOAD);

  return Object.entries(jobsTableColumns).reduce(
    (obj, [key, column]) => {
      if (!excludedKeys.includes(key as JobsTableColumnKey)) {
        obj[key as JobsTableColumnKey] = column;
      }
      return obj;
    },
    {} as typeof jobsTableColumns,
  );
};

// The common rate-type labels ("Per Load", "Per Ton", "Per Hour") fit at the
// table's 12px font in 100px once the select's 14px left and 30px right
// padding are subtracted; rarer long labels ellipsize in the closed field and
// stay fully readable in the open menu. RateAndTypeInput's built-in 150px
// width is sized for full-width dialog forms, not a table cell.
const INLINE_RATE_TYPE_SELECT_WIDTH = '100px';

// Fits the condensed inline pair: the 100px rate-type select plus the value
// input's 48px minimum, the outlined input's paddings/borders, and the cell
// gutters — the read-mode 120px Rates column cannot hold it. Fixed rather
// than flexed: a rate is a short number, and a stretched value input reads
// as a text field.
export const EDIT_MODE_JOB_RATES_COLUMN_SIZE = '190px';

// Notes gets a usable multi-line floor while the row is editable (its 90px
// read-mode width fits only the truncated preview text) and takes the row's
// largest flex share — it is the widest editable field in the row.
export const EDIT_MODE_NOTES_COLUMN_SIZE = 'minmax(150px, 4fr)';

// Pickup & Dropoff is read-only in edit mode — two short site names. Its
// read-mode 5fr share otherwise balloons at wide viewports and strands the
// notes input beside a band of empty space.
export const EDIT_MODE_WAYPOINTS_COLUMN_SIZE = '2fr';

// The materials typeahead needs room for real material names while editing;
// the shared 2.5fr read-mode share gets squeezed by the row's other inputs.
export const EDIT_MODE_MATERIALS_COLUMN_SIZE = '3.5fr';

// The edit-mode header hosts the Cancel + Save buttons in the actions cell:
// ~88px together at their 10px font and paddings. Sized to just fit them so
// the body rows (which only show the job id) don't carry dead width.
export const EDIT_MODE_ACTIONS_COLUMN_SIZE = '95px';

export const makeJobsTableGridTemplateColumns = (
  isEditingJobs: boolean,
  showDispatchJobsTruckAndTrailerColumnSettingEnabled: boolean,
  showDispatchJobsMaterialsColumnSettingEnabled: boolean,
  showDispatchJobsPickupDropoffColumnSettingEnabled: boolean,
) => {
  const columns = getJobsTableColumns(
    isEditingJobs,
    showDispatchJobsTruckAndTrailerColumnSettingEnabled,
    showDispatchJobsMaterialsColumnSettingEnabled,
    showDispatchJobsPickupDropoffColumnSettingEnabled,
  );

  return Object.entries(columns)
    .map(([key, { size }]) => {
      if (key === JobsTableColumnKey.JOB_RATES && isEditingJobs) {
        return EDIT_MODE_JOB_RATES_COLUMN_SIZE;
      }

      if (key === JobsTableColumnKey.NOTES && isEditingJobs) {
        return EDIT_MODE_NOTES_COLUMN_SIZE;
      }

      if (key === JobsTableColumnKey.MATERIALS && isEditingJobs) {
        return EDIT_MODE_MATERIALS_COLUMN_SIZE;
      }

      if (key === JobsTableColumnKey.ACTIONS && isEditingJobs) {
        return EDIT_MODE_ACTIONS_COLUMN_SIZE;
      }

      if (key === JobsTableColumnKey.WAYPOINTS && isEditingJobs) {
        return EDIT_MODE_WAYPOINTS_COLUMN_SIZE;
      }

      return size;
    })
    .join(' ');
};

interface JobsTableColumnProps {
  filterDate?: string;
  isDisabled?: boolean;
  isHovered?: boolean;
  job: Job;
}

interface CheckboxColumnProps extends JobsTableColumnProps {
  isChecked?: boolean;
  onCheckedStateChange?: () => void;
}

export function CheckboxColumn({
  isChecked,
  onCheckedStateChange,
  isDisabled,
}: CheckboxColumnProps) {
  const isGridRow = useContext(JobsTablePresentationContext) === 'grid';

  return (
    <Column columnKey={JobsTableColumnKey.CHECKBOX}>
      <Checkbox
        isChecked={isChecked}
        isDisabled={isDisabled}
        onChange={onCheckedStateChange}
        sx={isGridRow ? undefined : { [`&.${checkboxClasses.root}`]: { mt: '-10px' } }}
        data-ui="page-dispatch__actions__checkbox"
      />
    </Column>
  );
}

export function GPSStatusColumn({ job: jobProp }: JobsTableColumnProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { getJobDeviceStatus, getJobById } = useJob();

  const [job, setJob] = useState(jobProp);

  const isActive = job.isActive;

  return (
    <Column columnKey={JobsTableColumnKey.GPS_STATUS}>
      {isActive && job.gpsStatus && (
        <GpsStatusPopover
          gpsStatus={job.gpsStatus}
          showIdleBadge={false}
          onGetDeviceStatus={async () => {
            const deviceStatus = await getJobDeviceStatus(job.id, companyId);
            if (job.gpsStatus?.status !== deviceStatus?.gpsStatus?.status) {
              // By Santiago's suggestion: This is a workaround to update the job with the latest GPS status
              // Currently, there's no RTU to update the job with the latest GPS status
              getJobById(job.id, companyId).then((job) => {
                if (job) {
                  setJob(job);
                }
              });
            }
            return deviceStatus;
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        />
      )}
    </Column>
  );
}

export const VendorAndDriverColumn = observer(
  ({ isHovered, job, filterDate, isDisabled = false }: JobsTableColumnProps) => {
    const theme = useTheme();
    const isGridRow = useContext(JobsTablePresentationContext) === 'grid';
    const driverDetailSx = {
      color: theme.brandV2.colors.treadGray2,
      fontSize: '11px',
      ...(isGridRow ? { ml: 0.5 } : {}),
    };
    const { userStore, ordersDispatchStore } = useStores();
    const { canAssignJob } = userStore.getPermissions();

    const hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled = useFeatureFlag({
      featureFlagKey: FeatureFlags.hideDriversVendorNameIfOwnerOperator,
    });
    const hideDriverEquipmentWhenJobHasEquipmentSettingEnabled =
      userStore.companySettings.isEnabled('hide_driver_equipment_when_job_has_equipment');
    const showDispatchJobsDriverLabelsSettingEnabled =
      userStore.companySettings.isEnabled('show_dispatch_jobs_driver_labels');

    const { driver, editable, stateEvents, vendorJobAssignment } = job;
    const hasVendorOrDriver = Boolean(vendorJobAssignment?.vendorAccount?.name || driver);
    const isAcceptable = canAcceptJob(job);
    const isJobInDoneState = DONE_JOB_STATES.includes(job.status);

    let content: ReactNode = null;

    const handleLoadingStatusChange = useCallback(
      (isLoading: boolean) => {
        if (isLoading) {
          ordersDispatchStore.orderJobUpdateStart(job.id);
        }
      },
      [job.id],
    );

    const handleAssigneeChange = useCallback(
      (updatedJob: Job) => {
        ordersDispatchStore.orderJobUpdateEnd(job.id, updatedJob);
      },
      [job.id],
    );

    const handleAssigneeChangeFailed = useCallback(() => {
      ordersDispatchStore.orderJobUpdateEnd(job.id);
    }, [job.id]);

    const vendorTypeLabel = vendorJobAssignment?.vendorEquipmentType && (
      <OverflowAwareText sx={driverDetailSx}>
        {isGridRow && '• '}
        {t('dispatch.job.vendor_type', {
          name: vendorJobAssignment.vendorEquipmentType.name,
        })}
      </OverflowAwareText>
    );

    if (hasVendorOrDriver) {
      const isUnassignable =
        !isDisabled &&
        Boolean(editable && canAssignJob) &&
        stateEvents.includes(JobStateEvent.UNASSIGN);

      let innerContent;

      if (driver) {
        const driverInfo = getDriverInfoForCards(
          driver,
          vendorJobAssignment?.vendorAccount?.connectedCompany ?? undefined,
          hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled,
        );

        const shouldShowDriverEquipment =
          hideDriverEquipmentWhenJobHasEquipmentSettingEnabled
            ? job.equipment
              ? false
              : Boolean(driverInfo.equipmentName)
            : Boolean(driverInfo.equipmentName);

        innerContent = (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <OverflowAwareText
                variant="body2"
                sx={{
                  color: driverInfo.isAvailable
                    ? theme.brandV2.colors.treadBlue2
                    : theme.brandV2.colors.treadBlack,
                  cursor: 'auto',
                  fontWeight: 600,
                  width: 'auto',
                }}
              >
                {shouldShowDriverEquipment && (
                  <>
                    <Typography
                      component="span"
                      sx={{
                        color: theme.brandV2.colors.treadBlack,
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                      }}
                    >
                      {driverInfo.equipmentName}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        color: theme.brandV2.colors.treadBlack,
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                      }}
                    >
                      {' • '}
                    </Typography>
                  </>
                )}

                <Typography
                  component="span"
                  sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
                >
                  {driverInfo.fullName}
                </Typography>
              </OverflowAwareText>
            </Box>

            {driverInfo.truckType && (
              <OverflowAwareText sx={driverDetailSx}>
                {isGridRow && '• '}
                {driverInfo.truckType}
              </OverflowAwareText>
            )}

            {driverInfo.vendorName && (
              <OverflowAwareText sx={driverDetailSx}>
                {isGridRow && '• '}
                {driverInfo.vendorName}
              </OverflowAwareText>
            )}

            {vendorTypeLabel}

            {showDispatchJobsDriverLabelsSettingEnabled && !isGridRow && (
              <LabelList
                compact
                disableTooltips
                labels={driver.labels}
                sx={{ flexWrap: 'wrap' }}
              />
            )}
          </Box>
        );
      } else {
        const isVendorAvailable = vendorJobAssignment?.vendorAccount?.scheduleOn;

        innerContent = (
          <Box
            sx={{ display: 'flex', flex: 1, flexDirection: 'column', overflow: 'hidden' }}
          >
            <OverflowAwareText
              sx={{
                color: isVendorAvailable
                  ? theme.brandV2.colors.treadBlue2
                  : theme.brandV2.colors.treadBlack,
                fontWeight: 600,
                width: 'auto',
              }}
            >
              {vendorJobAssignment?.vendorAccount?.name}
            </OverflowAwareText>
            {vendorTypeLabel}
          </Box>
        );
      }

      content = (
        <Box
          sx={[
            {
              alignSelf: 'flex-start',
              borderRight: 'solid 1px transparent',
              display: 'flex',
              gap: 1,
              height: '100%',
            },
            isUnassignable && isHovered
              ? {
                  borderRightColor: theme.brandV2.colors.treadGray7,
                  '& .action-box': { display: 'flex', opacity: 1 },
                }
              : {},
          ]}
        >
          {driver?.fullName ? (
            <Tooltip
              disableInteractive
              title={
                <>
                  <div>
                    {t('form_fields.driver')}: {driver.fullName}
                  </div>

                  {driver?.equipment && (
                    <div>
                      {t('form_fields.equipment')}: {getConsolidatedEquipmentName(driver)}{' '}
                      • {driver.equipment.equipmentType.name}
                    </div>
                  )}

                  {driver.phone && (
                    <div>
                      {t('common.phone')}: {driver.phone}
                    </div>
                  )}

                  {job.equipment?.licenseNumber && (
                    <div>
                      {t('common.license')}: {job.equipment.licenseNumber}{' '}
                    </div>
                  )}

                  {showDispatchJobsDriverLabelsSettingEnabled &&
                    driver.labels.length > 0 && (
                      <div>
                        {t('form_fields.labels')}:{' '}
                        {driver.labels.map((label) => label.name).join(', ')}
                      </div>
                    )}
                </>
              }
              placement={'bottom-start'}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -14],
                      },
                    },
                  ],
                },
              }}
            >
              {innerContent}
            </Tooltip>
          ) : (
            innerContent
          )}

          {isUnassignable && (
            <Box
              className="action-box"
              sx={{
                alignSelf: 'center',
                pr: '2px',
                ...(isGridRow
                  ? {
                      display: 'flex',
                      flexShrink: 0,
                      opacity: 0,
                      '&:focus-within': { opacity: 1 },
                    }
                  : { display: 'none', flex: 0 }),
              }}
            >
              <JobAssigneeHandler
                filterDate={filterDate}
                job={job}
                onAssigneeChange={handleAssigneeChange}
                onAssigneeChangeFailed={handleAssigneeChangeFailed}
                onLoadingStatusChange={handleLoadingStatusChange}
              />
            </Box>
          )}
        </Box>
      );
    } else if (!isAcceptable && !isDisabled && canAssignJob && !isJobInDoneState) {
      content = (
        <JobAssigneeHandler
          filterDate={filterDate}
          job={job}
          onAssigneeChange={handleAssigneeChange}
          onAssigneeChangeFailed={handleAssigneeChangeFailed}
          onLoadingStatusChange={handleLoadingStatusChange}
        />
      );
    }

    return <Column columnKey={JobsTableColumnKey.VENDOR_AND_DRIVER}>{content}</Column>;
  },
);

interface StatusColumnProps extends JobsTableColumnProps {
  onAccept: () => void;
  onReject: () => void;
  onSend: () => void;
  isDisabled?: boolean;
}

type AvailableAction = {
  color: ButtonProps['color'];
  icon?: ReactNode;
  isDisabled?: boolean;
  label: string;
  onClick: () => Promise<void>;
};

export const StatusColumn = observer(
  ({ job, onAccept, onReject, onSend, isDisabled = false }: StatusColumnProps) => {
    const theme = useTheme();
    const { userStore, toasterStore, ordersDispatchStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);

    const { doEvent, getJobById } = useJob();
    const [jobCTAIsBusy, setJobCTAIsBusy] = useState(false);
    const [isTransitioningToStatus, setIsTransitioningToStatus] =
      useState<JobStateEvent>();
    const isPaused = Boolean(job.pausedAt);

    const jobCTAs = useMemo(() => {
      const isAcceptable = canAcceptJob(job);
      const isOptionsDisabled =
        ordersDispatchStore.isAcceptingOrRejectingJobs.get(job.id) ?? false;

      // Vendor actions
      if (isAcceptable) {
        return [
          {
            color: 'brandV2Green',
            isDisabled: isOptionsDisabled,
            label: t('actions.accept'),
            onClick: onAccept,
          } as AvailableAction,
          {
            color: 'brandV2Red',
            isDisabled: isOptionsDisabled,
            label: t('actions.decline'),
            onClick: onReject,
          } as AvailableAction,
        ];
      }

      // Driver or Vendor actions
      if (isJobSendable(job)) {
        return [
          {
            color: 'brandV2Green',
            icon: <SendOutlined />,
            label: t('actions.send'),
            onClick: onSend,
          } as AvailableAction,
        ];
      }

      return null;
    }, [
      job,
      ordersDispatchStore.isSendingJobs.get(job.id),
      ordersDispatchStore.isAcceptingOrRejectingJobs.get(job.id),
    ]);

    const transitionableStates = getJobNextEvents(job);

    const handleTransitionToStatus = useCallback(
      async (status: JobStateEvent) => {
        setIsTransitioningToStatus(status);

        try {
          const updatedJob = await doEvent(job.id, status, companyId);

          if (updatedJob) {
            ordersDispatchStore.upsertJob(updatedJob);
          }

          if (isJobAssignmentCancelAction(status)) {
            trackJobAssignmentCancelled({
              action: status,
              jobIds: [job.id],
              source: 'status_menu',
            });
          }

          toasterStore.push(
            alert(t('dispatch.job.updated', { name: job.jobId }), AlertTypes.success),
          );
        } catch (error) {
          const errorStatus = isAxiosError(error) ? error.response?.status : undefined;
          const is422Error = errorStatus === 422;
          const is409ConflictError = errorStatus === 409;

          // A 409 means the job's server-side state moved on (e.g. it was already
          // transitioned in another tab or by another user) while this row still
          // offered a stale set of actions. Refetch the job so its state_events
          // refresh and the now-invalid action disappears from the menu.
          if (is409ConflictError) {
            try {
              const latestJob = await getJobById(job.id, companyId);

              if (latestJob) {
                ordersDispatchStore.upsertJob(latestJob);
              }
            } catch {
              // getJobById reports its own failure; keep surfacing the transition error.
            }
          }

          if (is422Error) {
            const formattedError = error as AxiosError<ModelError_Response>;
            const errorMessage = formattedError.response?.data.error.errors[0]?.message;
            const isMissingEquipmentError =
              errorMessage === 'Equipment required for a job to start.';

            if (isMissingEquipmentError) {
              toasterStore.push(
                alert(
                  t('error_messages.jobs.failed_to_enroute_job_due_to_missing_equipment'),
                  AlertTypes.error,
                ),
              );
            } else {
              connection.handleRequestError(
                error,
                t('error_messages.jobs.failed_to_do_event', { event: status }),
              );
            }
          } else {
            connection.handleRequestError(
              error,
              t('error_messages.jobs.failed_to_do_event', { event: status }),
            );
          }
        } finally {
          setIsTransitioningToStatus(undefined);
        }
      },
      [
        companyId,
        doEvent,
        getJobById,
        job.id,
        job.jobId,
        ordersDispatchStore,
        toasterStore,
      ],
    );

    const handleJobCTAClick = useCallback(
      async (event: React.MouseEvent, onClickCallback: AvailableAction['onClick']) => {
        event.stopPropagation();

        try {
          setJobCTAIsBusy(true);
          await onClickCallback();
        } finally {
          setJobCTAIsBusy(false);
        }
      },
      [],
    );

    return (
      <Column columnKey={JobsTableColumnKey.STATUS}>
        <Box
          sx={{
            alignItems: 'center',
            alignSelf: 'flex-start',
            display: 'flex',
            gap: 0.5,
          }}
        >
          {isPaused && <JobPausedIndicator />}

          {jobCTAs ? (
            <>
              {jobCTAs.map((cta) => (
                <SmallButton
                  key={cta.label}
                  color={cta.color}
                  disabled={cta.isDisabled || isDisabled || jobCTAIsBusy}
                  onClick={(event) => handleJobCTAClick(event, cta.onClick)}
                  startIcon={cta.icon}
                  sx={{
                    '&.MuiButtonBase-root': {
                      height: '24px',
                      px: theme.spacing(0.5),
                      '&.Mui-disabled': { pointerEvents: 'auto' },
                      '.MuiSvgIcon-root': { fontSize: '16px' },
                    },
                  }}
                >
                  {cta.label}
                </SmallButton>
              ))}
            </>
          ) : (
            <Box>
              <JobStatusTransitioner
                closeOnTransitionComplete
                isTransitioningToStatus={isTransitioningToStatus}
                job={job}
                onTransitionToStatus={handleTransitionToStatus}
                transitionableStates={transitionableStates}
                isDisabled={isDisabled}
              />
            </Box>
          )}
        </Box>
      </Column>
    );
  },
);

export function RecurringLoadsColumn({ job }: JobsTableColumnProps) {
  const form = useFormContext<EditJobTableRowsDto>();
  const recurringLoadsFieldName: `${string}.recurringLoads` = `${job.id}.recurringLoads`;
  const watchedJobUnitOfMeasure = form.watch(`${job.id}.unitOfMeasure`);
  const watchedQuantity = form.watch(`${job.id}.jobQuantity`) as string | number | null;
  const watchedEquipmentTypeGrossCapacity = form.watch(
    `${job.id}.equipmentTypeGrossCapacity`,
  ) as string | number | null;
  const isLoadsUoM = watchedJobUnitOfMeasure?.id === OrderUnitOfMeasure.LOAD;

  useEffect(() => {
    if (!form.formState.isDirty) return;
    if (!isLoadsUoM) {
      const isRunning = shouldEnableRecurringLoads(
        watchedQuantity,
        watchedEquipmentTypeGrossCapacity,
        watchedJobUnitOfMeasure?.name,
      );
      form.setValue(recurringLoadsFieldName, isRunning);
    }
  }, [
    isLoadsUoM,
    watchedQuantity,
    watchedEquipmentTypeGrossCapacity,
    watchedJobUnitOfMeasure?.name,
  ]);

  return (
    <Column columnKey={JobsTableColumnKey.RECURRING_LOADS}>
      <CheckBoxFormField
        control={form.control}
        label={t('common.running')}
        name={recurringLoadsFieldName}
        value={Boolean(form.getValues(recurringLoadsFieldName))}
        sx={{
          mt: '6px',
          [`& .${formControlLabelClasses.root}`]: { mx: 0 },
          [`& .${checkboxClasses.root}`]: { pl: 0, pr: 0.25, p: 0 },
          [`& .${typographyClasses.root}`]: { fontSize: '12px' },
        }}
      />
    </Column>
  );
}

interface StartColumnProps extends JobsTableColumnProps {
  isEditingJobsTableRows?: boolean;
}

export function StartColumn({ isEditingJobsTableRows, job }: StartColumnProps) {
  const startTime = getJobStartTimeText(job) || null;
  const startDate = getJobStartDateText(job) || null;

  const form = useFormContext<EditJobTableRowsDto>();
  const jobStartTimeFieldName = `${job.id}.jobStartTime`;
  const jobStartTime = form.watch(jobStartTimeFieldName);

  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    try {
      jobStartTimeSchema.validateSync(jobStartTime);
      setErrorMessage(undefined);
    } catch (error) {
      const e = error as yup.ValidationError;

      setErrorMessage(e.message);
    }
  }, [jobStartTimeFieldName, jobStartTime]);

  if (isEditingJobsTableRows) {
    return (
      <Column columnKey={JobsTableColumnKey.START}>
        <FlexColumn>
          <TimeInput
            control={form.control}
            isRequired
            manualError={errorMessage}
            name={jobStartTimeFieldName}
            sx={{
              [`& .${inputBaseClasses.input}`]: { pl: '10px', py: '6px' },
              [`& .${iconButtonClasses.root}`]: { p: '4px' },
              [`& .${svgIconClasses.root}`]: { fontSize: '15px' },
            }}
          />
        </FlexColumn>
      </Column>
    );
  }

  return (
    <Column columnKey={JobsTableColumnKey.START}>
      {(startTime || startDate) && (
        <FlexColumn>
          {startTime && <OverflowAwareText>{startTime}</OverflowAwareText>}
          {startDate && <OverflowAwareText>{startDate}</OverflowAwareText>}
        </FlexColumn>
      )}
    </Column>
  );
}

export function CycleTimeColumn({ job }: JobsTableColumnProps) {
  const formatted = getJobCycleTimeText(job);

  return (
    <Column columnKey={JobsTableColumnKey.CYCLE_TIME}>
      <FlexColumn>
        {Boolean(formatted) && <OverflowAwareText>{formatted}</OverflowAwareText>}
        <EtaCell eta={job.eta} jobId={job.id} />
      </FlexColumn>
    </Column>
  );
}

export function RequestedColumn({ job }: JobsTableColumnProps) {
  const isRunningLoads =
    Boolean(job.recurringLoads) && job.unitOfMeasure?.id === OrderUnitOfMeasure.LOAD;
  const loadsCount = job.requestedLoadsCount ?? null;

  const jobQty = job.quantity ? numberToIntegerOrDecimal(Number(job.quantity)) : null;
  const jobUoMName = job.unitOfMeasure?.name ? startCase(job.unitOfMeasure.name) : null;

  const shouldShowLoadsCount = Boolean(
    loadsCount && job.unitOfMeasure?.id === OrderUnitOfMeasure.LOAD,
  );
  const shouldShowJobQty = Boolean(jobQty && jobUoMName && !shouldShowLoadsCount);

  return (
    <Column columnKey={JobsTableColumnKey.REQUESTED}>
      <FlexColumn>
        {/* Show "Running (n+)" if there is a target quantity, running is checked, and UoM is load */}
        {/* Show "Running" if running is checked, UoM load, and there's no target quantity */}
        {/* Otherwise, show job quantity or loads count or requested loads count */}
        {isRunningLoads ? (
          <OverflowAwareText>
            {(jobQty && Number(job.quantity) > 1) || shouldShowLoadsCount
              ? shouldShowLoadsCount && loadsCount === 1
                ? t('common.running')
                : `${t('common.running')} (${shouldShowLoadsCount ? loadsCount : jobQty}+)`
              : t('common.running')}
          </OverflowAwareText>
        ) : shouldShowLoadsCount ? (
          <OverflowAwareText>
            <b>{loadsCount}</b>

            <Typography
              component="span"
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray3,
                fontSize: 'inherit',
              })}
            >{` ${t('form_fields.load')}${loadsCount && loadsCount > 1 ? 's' : ''}`}</Typography>
          </OverflowAwareText>
        ) : shouldShowJobQty ? (
          <OverflowAwareText>
            <b>{jobQty}</b>

            <Typography
              component="span"
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray3,
                fontSize: 'inherit',
              })}
            >
              {` ${jobUoMName}`}
            </Typography>
          </OverflowAwareText>
        ) : null}
      </FlexColumn>
    </Column>
  );
}

export function DeliveredColumn({ job }: JobsTableColumnProps) {
  const ticketsCount = job.jobSummary?.loadsWithTicketsCount ?? 0;
  const deliveredQuantities = job.jobSummary?.deliveredQuantities ?? [];

  const hasUomMismatch = jobHasAnyUomMismatch(
    job.equipmentTypeGrossCapacityUnitOfMeasure,
    job.loadUnitOfMeasure,
    deliveredQuantities,
    job.unitOfMeasure,
  );

  const { total: largestSumTotal, unitOfMeasure: largestSumUoM } =
    getLargestDeliveredQuantitySum(deliveredQuantities);
  const formattedLargestSumTotal = largestSumTotal.toFixed(2);
  const shouldShowSum = ticketsCount > 0 && largestSumTotal > 0;

  const ticketsTooltipContent = (
    <>
      {t('dispatch.dispatch_v2.tickets')}: {ticketsCount}
      {shouldShowSum ? (
        <>
          <br />
          {t('project.form.total_quantity')}: {formattedLargestSumTotal}
          {largestSumUoM ? ` ${startCase(largestSumUoM)}` : ''}
        </>
      ) : null}
    </>
  );

  return (
    <Column columnKey={JobsTableColumnKey.DELIVERED}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={t('dispatch.dispatch_v2.completed_loads')} placement="right">
          <OverflowAwareText
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}
          >
            <CheckCircleOutlined sx={{ fontSize: '12px', height: 16, width: 16 }} />
            {job.jobSummary?.completedLoadsCount ?? job.completedLoadsCount}
          </OverflowAwareText>
        </Tooltip>
        {hasUomMismatch && <UomMismatchIndicator />}
      </Box>

      <Tooltip title={ticketsTooltipContent} placement="right">
        <OverflowAwareText
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: 'fit-content' }}
        >
          <ReceiptOutlined sx={{ fontSize: '12px', height: 16, width: 16 }} />
          {`${ticketsCount}${shouldShowSum ? ` • ${formattedLargestSumTotal}` : ''}`}
        </OverflowAwareText>
      </Tooltip>
    </Column>
  );
}

interface RequestedEquipmentTypeColumnProps extends JobsTableColumnProps {
  order: Order;
}

export const RequestedEquipmentTypeColumn = observer(
  ({ job, order }: RequestedEquipmentTypeColumnProps) => {
    const theme = useTheme();
    const { toasterStore } = useStores();
    const { updateJobEquipment } = useOrdersDispatch();

    const isOrderEditable = Boolean(order?.editable);

    const handleChange = useCallback(
      async (requestedEquipmentType?: RequestedEquipmentTypeSelectorOption) => {
        try {
          const updatedJob = await updateJobEquipment(
            job.id,
            undefined,
            undefined,
            requestedEquipmentType?.name ?? null,
            requestedEquipmentType?.accountingId ?? null,
          );

          if (updatedJob) {
            toasterStore.push(
              alert(
                t('dispatch.job.updated', { name: updatedJob?.jobId }),
                AlertTypes.success,
              ),
            );
          }
        } catch {
          // Error is handled by the hook
        }
      },
      [job.id],
    );

    return (
      <Column columnKey={JobsTableColumnKey.REQUESTED_EQUIPMENT_TYPE}>
        <FlexColumn sx={{ alignItems: 'flex-start' }}>
          <RequestedEquipmentTypeSelector
            isDisabled={!isOrderEditable}
            job={job}
            onChange={handleChange}
            placeholder={t('form_fields.requested_equipment_type')}
            requestedEquipmentType={job.requestedEquipmentTypeName}
          />
          {job.customerJobAssignment?.vendorEquipmentType && (
            <OverflowAwareText
              sx={{
                color: theme.brandV2.colors.treadGray2,
                fontSize: '11px',
                maxWidth: '100%',
              }}
            >
              {t('dispatch.job.vendor_type', {
                name: job.customerJobAssignment.vendorEquipmentType.name,
              })}
            </OverflowAwareText>
          )}
        </FlexColumn>
      </Column>
    );
  },
);

export const TruckAndTrailerColumn = observer(({ job }: JobsTableColumnProps) => {
  const { userStore, toasterStore } = useStores();
  const { updateJobEquipment } = useOrdersDispatch();
  const companyId = userStore.currentCompanies[0].id || userStore.userCompany?.id;

  const updateEquipment = useCallback(
    async (equipmentId?: string | null, additionalEquipmentIds?: string[]) => {
      try {
        const updatedJob = await updateJobEquipment(
          job.id,
          equipmentId,
          additionalEquipmentIds,
        );

        if (updatedJob) {
          toasterStore.push(
            alert(
              t('dispatch.job.updated', { name: updatedJob?.jobId }),
              AlertTypes.success,
            ),
          );
        }
      } catch {
        // Error is handled by the hook
      }
    },
    [job.id],
  );

  const handleSelectedTruckChange = useCallback((equipmentId?: string) => {
    void updateEquipment(equipmentId ?? null);
  }, []);

  const handleSelectedTrailerChange = useCallback((equipmentId?: string) => {
    void updateEquipment(undefined, equipmentId ? [equipmentId] : []);
  }, []);

  return (
    <Column columnKey={JobsTableColumnKey.TRUCK_AND_TRAILER}>
      <FlexColumn sx={{ alignItems: 'flex-start' }}>
        <EquipmentSelector
          companyId={companyId}
          equipment={job.equipment}
          job={job}
          onChange={handleSelectedTruckChange}
          placeholder={t('form_fields.truck')}
          allowRemovalInActiveStates={false}
        />

        <EquipmentSelector
          companyId={companyId}
          equipment={job.additionalEquipment}
          job={job}
          onChange={handleSelectedTrailerChange}
          placeholder={t('form_fields.trailer')}
          allowRemovalInActiveStates={true}
        />
      </FlexColumn>
    </Column>
  );
});

interface MaterialsColumnProps extends JobsTableColumnProps {
  isEditingJobsTableRows?: boolean;
}

const MaterialsEditColumn = observer(({ job }: JobsTableColumnProps) => {
  const form = useFormContext<EditJobTableRowsDto>();
  const { userStore } = useStores();
  const { canEditJob } = userStore.getPermissions();
  const { getMaterialsByCompanyIdTypeahead } = useMaterials();
  const companyId = getEffectiveUserCompanyId(userStore);
  const materialFieldName: `${string}.material` = `${job.id}.material`;
  const isMaterialEditingAllowed =
    job.editable && canEditJob && !userStore.isViewOnlyUser;

  return (
    <Column columnKey={JobsTableColumnKey.MATERIALS}>
      <Box
        onClick={(event) => event.stopPropagation()}
        data-ui={`page-dispatch__actions__job-${job.jobId}__button`}
      >
        <AutocompleteAsyncFormField
          asyncCallback={getMaterialsByCompanyIdTypeahead}
          clearable
          control={form.control}
          disabled={!isMaterialEditingAllowed}
          errors={form.formState.errors}
          extraRequestOptions={{ companyId, orderId: job.order?.id }}
          getLabel={(item) => item.name || ''}
          getValue={(item) => item.id}
          hideLabel
          label={t('form_fields.material')}
          name={materialFieldName}
          sx={{
            fontSize: '12px',
            '& .MuiAutocomplete-inputRoot': { fontSize: '12px', width: '100%' },
            '& .MuiAutocomplete-endAdornment': {
              right: '5px !important',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 'fit-content',
            },
          }}
          dataUi={`page-dispatch__jobs-table__job-${job.jobId}__material-input`}
        />
      </Box>
    </Column>
  );
});

export function MaterialsColumn({ isEditingJobsTableRows, job }: MaterialsColumnProps) {
  const material = getJobMaterialText(job);

  if (isEditingJobsTableRows) {
    return <MaterialsEditColumn job={job} />;
  }

  return (
    <Column columnKey={JobsTableColumnKey.MATERIALS}>
      {Boolean(material) && (
        <Tooltip title={material}>
          <OverflowAwareText fontSize="inherit" component="span">
            {material}
          </OverflowAwareText>
        </Tooltip>
      )}
    </Column>
  );
}

export function WaypointsColumn({ job }: JobsTableColumnProps) {
  const pickUp = extractWaypointData(job.pickUpWayPoints?.[0]);
  const dropOff = extractWaypointData(job.dropOffWayPoints?.[0]);

  return (
    <Column columnKey={JobsTableColumnKey.WAYPOINTS}>
      <FlexColumn>
        {[pickUp, dropOff].map((site, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
            }}
          >
            <GeofenceStartAdornment
              geofenceType={site?.geofenceType}
              isLoading={false}
              sx={{ pr: 0.5 }}
            />
            <OverflowAwareText>{site?.name || '-'}</OverflowAwareText>
          </Box>
        ))}
      </FlexColumn>
    </Column>
  );
}

interface JobRatesColumnProps extends JobsTableColumnProps {
  order: Order;
}

// Stands in for a party whose rate source is withheld, so the three parties can be built as
// one object without repeating the empty shape.
const NO_RATE_SOURCE = { rateCard: null, rate: null };

export function JobRatesColumn({ isHovered, job, order }: JobRatesColumnProps) {
  const theme = useTheme();
  const { userStore } = useStores();
  const { currencyFormatter } = useCompanyCurrency();
  const shouldShowHaulerRateCard = useShouldShowHaulerRateCard();
  const isLiveEtaEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.liveEtaEnabled,
  });
  const isPayCommissionsEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.payCommissions,
  });
  const { canEditJob, canViewRate } = userStore.getPermissions();
  const hasAnyEditableRateSide = Object.values(job.rateEditable).some(Boolean);
  // Hauler mode is exempt from the per-side locks: its dialog edits only the hauler
  // rate type, which no approved invoice locks and which has no other entry point.
  const isEditable =
    job.editable && canEditJob && (shouldShowHaulerRateCard || hasAnyEditableRateSide);
  const isEditableAndHovered = isEditable && isHovered;
  const canOnlySeeDriverRates = userStore.isDriver;
  const canSeePayCommissions =
    isPayCommissionsEnabled &&
    canViewRate &&
    !canOnlySeeDriverRates &&
    !shouldShowHaulerRateCard;
  const vendorCommission = jobPayCommission(job, 'vendor');
  const driverCommission = jobPayCommission(job, 'driver');
  const vendorCommissionPercentage = canSeePayCommissions
    ? toRateNumber(vendorCommission.percentage)
    : null;
  const driverCommissionPercentage = canSeePayCommissions
    ? toRateNumber(driverCommission.percentage)
    : null;

  const makeRateBlock = (
    job: Job,
    target: 'customer' | 'haul' | 'pay' | 'driver' | 'hauler',
    viewAsDriver?: boolean,
  ) => {
    const rateInfo = extractRateInfoFromJob(job, target);

    if (rateInfo) {
      const formattedValue = getFormattedRateValue(
        rateInfo.rateType,
        rateInfo.rateValue,
        currencyFormatter,
        true,
      );
      const rateTypeLabel = t(`rate_types.${snakeCase(rateInfo.rateType)}`);

      if (target === 'customer' || target === 'haul') {
        return (
          <OverflowAwareText sx={{ color: theme.brandV2.colors.treadGreen }}>
            {formattedValue ? `+${formattedValue} ${rateTypeLabel}` : rateTypeLabel}
          </OverflowAwareText>
        );
      }

      if (target === 'hauler') {
        return <OverflowAwareText>{rateTypeLabel}</OverflowAwareText>;
      }

      if (viewAsDriver) {
        return (
          <OverflowAwareText sx={{ color: theme.brandV2.colors.treadGreen }}>
            {formattedValue ? `+${formattedValue} ${rateTypeLabel}` : rateTypeLabel}
          </OverflowAwareText>
        );
      }

      const commissionPercentage =
        target === 'pay' ? vendorCommissionPercentage : driverCommissionPercentage;

      if (formattedValue && commissionPercentage) {
        const parenthesizedGrossRate = formatRateWithType(
          rateInfo.rateValue,
          rateInfo.rateType,
          (amount) => `(${currencyFormatter(amount)})`,
          { unitStyle: 'short' },
        );

        return (
          <OverflowAwareText sx={{ whiteSpace: 'normal' }}>
            {t('pay_commission.cell_percentage', { percentage: commissionPercentage })}
            <Typography
              component="span"
              sx={{ color: theme.brandV2.colors.treadRed, fontSize: 'inherit' }}
            >
              {parenthesizedGrossRate}
            </Typography>
          </OverflowAwareText>
        );
      }

      return (
        <OverflowAwareText sx={{ color: theme.brandV2.colors.treadRed }}>
          {formattedValue ? `(${formattedValue}) ${rateTypeLabel}` : rateTypeLabel}
        </OverflowAwareText>
      );
    }

    return null;
  };

  const customerRateBlock = canOnlySeeDriverRates ? null : makeRateBlock(job, 'customer');
  const haulRateBlock = canOnlySeeDriverRates ? null : makeRateBlock(job, 'haul');
  const payRateBlock = canOnlySeeDriverRates ? null : makeRateBlock(job, 'pay');
  const driverRateBlock = makeRateBlock(job, 'driver', canOnlySeeDriverRates);
  const haulerRateBlock = canOnlySeeDriverRates ? null : makeRateBlock(job, 'hauler');

  const isOrderOwner = userCompanyOwnsOrder(userStore, order);

  // A rate card lists every party's rate on it, so provenance is only shown to a viewer
  // already cleared to see all of them: the order's owner, and not a driver-scoped user.
  // Hauler mode hides every amount on this surface (the cell shows only a rate type, the
  // edit dialog only that field), so hover may not surface amounts there either. Everyone
  // else keeps the cell exactly as it was rather than being shown pricing the rest of the
  // column deliberately hides from them.
  const canSeeAllPartyRates =
    isOrderOwner && !canOnlySeeDriverRates && !shouldShowHaulerRateCard;
  // A card stamp can outlive the rate it priced, and the tooltip renders the stamped card's
  // OWN rates — so a party left holding a stamp with no rate behind it would be shown an
  // amount that appears nowhere on this job. Claim provenance only for a party whose rate the
  // cell actually displays. Read from the rate data rather than the rendered blocks so the
  // tooltip's source of truth cannot silently follow a change to the render path.
  const vendorRateInfo = extractRateInfoFromJob(job, 'pay');
  const driverRateInfo = extractRateInfoFromJob(job, 'driver');
  const hasCustomerRate = extractRateInfoFromJob(job, 'customer') !== null;
  const hasVendorRate = vendorRateInfo !== null;
  const hasDriverRate = driverRateInfo !== null;

  const payCommissions: PayCommissionBreakdown[] = compact([
    vendorRateInfo &&
      vendorCommissionPercentage && {
        grossRateValue: vendorRateInfo.rateValue,
        isLocked: Boolean(vendorCommission.locked),
        netRate: vendorCommission.netRate,
        party: 'vendor',
        percentage: vendorCommissionPercentage,
        rateType: vendorRateInfo.rateType,
        resolution: vendorCommission.resolution,
      },
    driverRateInfo &&
      driverCommissionPercentage && {
        grossRateValue: driverRateInfo.rateValue,
        isLocked: Boolean(driverCommission.locked),
        netRate: driverCommission.netRate,
        party: 'driver',
        percentage: driverCommissionPercentage,
        rateType: driverRateInfo.rateType,
        resolution: driverCommission.resolution,
      },
  ]);

  const provenanceSources = canSeeAllPartyRates
    ? {
        customer: hasCustomerRate
          ? {
              rateCard: job.customerJobAssignment?.siteRateSchedule ?? null,
              rate: job.customerJobAssignment?.rate ?? null,
            }
          : NO_RATE_SOURCE,
        driver: hasDriverRate
          ? { rateCard: job.driverSiteRateSchedule, rate: job.driverRate ?? null }
          : NO_RATE_SOURCE,
        vendor: hasVendorRate
          ? {
              rateCard: job.vendorJobAssignment?.siteRateSchedule ?? null,
              rate: job.vendorJobAssignment?.rate ?? null,
            }
          : NO_RATE_SOURCE,
      }
    : { customer: NO_RATE_SOURCE, driver: NO_RATE_SOURCE, vendor: NO_RATE_SOURCE };
  const defaultRateCardId = canSeeAllPartyRates
    ? (order.defaultSiteRateSchedule?.id ?? null)
    : null;

  const canEditCustomerRate = Boolean(job.customerJobAssignment);
  const canEditPayRate = Boolean(job.vendorJobAssignment);
  const isAssignedToVendor = Boolean(job.vendorJobAssignment?.id);
  const isAssignedToDriver = !isAssignedToVendor && Boolean(job.driver?.id);
  const canEditDriverRate = isAssignedToDriver && !job.driverRateType;
  const canEditHaulerRate = shouldShowHaulerRateCard;

  const isMissingVendorRate = canEditPayRate && !payRateBlock;
  const isMissingDriverRate = canEditDriverRate && !driverRateBlock;
  const isMissingHaulerRate = canEditHaulerRate && !haulerRateBlock;

  const isCustomerRateInPlace = canEditCustomerRate ? Boolean(customerRateBlock) : true;
  const isPayRateInPlace = canEditPayRate ? Boolean(payRateBlock) : true;
  const isDriverRateInPlace = canEditDriverRate ? Boolean(driverRateBlock) : true;
  const allRatesInPlace =
    isCustomerRateInPlace && isPayRateInPlace && isDriverRateInPlace;

  const ctaToShow: 'edit' | 'add' | null = useMemo(() => {
    if (shouldShowHaulerRateCard) {
      if (isMissingHaulerRate) {
        return 'add';
      }

      return 'edit';
    }

    if (haulerRateBlock) {
      const isAssigned = isAssignedToDriver || isAssignedToVendor;

      if (!isAssigned) {
        return null;
      }

      if (isMissingDriverRate || isMissingVendorRate) {
        return 'add';
      }

      return 'edit';
    }

    if (allRatesInPlace) {
      return 'edit';
    }

    return 'add';
  }, [
    allRatesInPlace,
    haulerRateBlock,
    isAssignedToDriver,
    isAssignedToVendor,
    isMissingDriverRate,
    isMissingHaulerRate,
    isMissingVendorRate,
    shouldShowHaulerRateCard,
  ]);

  const [showEditRatesDialog, setShowEditRatesDialog] = useState(false);

  const handleAddOrEditRateClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      setShowEditRatesDialog(true);
    },
    [job],
  );

  const etaRowTint = isLiveEtaEnabled ? etaRowTintBgcolor(theme, job.eta) : undefined;

  const EditRatesCTA = () => (
    <Box
      data-test-id={`page-dispatch__job-${job.jobId}__edit-rates-cta-background`}
      sx={{
        alignItems: 'center',
        bgcolor: theme.semantic.surfaceRaised,
        ...(etaRowTint
          ? { backgroundImage: `linear-gradient(${etaRowTint}, ${etaRowTint})` }
          : {}),
        borderRightColor: theme.brandV2.colors.treadGray7,
        borderRightWidth: '1px',
        bottom: 0,
        display: 'flex',
        position: 'absolute',
        right: 0,
        top: 0,
        width: '30px',
      }}
    >
      <Tooltip title={t('dispatch.job.edit_rates_dialog.cta_tooltip')}>
        <IconButton
          onClick={handleAddOrEditRateClick}
          sx={{ height: '26px', width: '26px' }}
          data-ui={`page-dispatch__actions__job-${job.jobId}__button`}
          data-test-id={`page-dispatch__job-${job.jobId}__edit-rates-cta`}
        >
          <EditOutlined
            sx={{ fontSize: '20px', color: theme.brandV2.colors.treadOrangeDark }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const AddRatesCTA = () => (
    <OverflowAwareText
      onClick={handleAddOrEditRateClick}
      sx={{ color: theme.brandV2.colors.treadOrangeDark, cursor: 'pointer' }}
    >
      {t('dispatch.job.edit_rates_dialog.add_rates_with_plus')}
    </OverflowAwareText>
  );

  return (
    <Column columnKey={JobsTableColumnKey.JOB_RATES}>
      <RateProvenanceTooltip
        defaultRateCardId={defaultRateCardId}
        payCommissions={payCommissions}
        sources={provenanceSources}
      >
        <FlexColumn>
          {shouldShowHaulerRateCard ? (
            haulerRateBlock
          ) : (
            <>
              {isOrderOwner ? customerRateBlock : null}
              {!isOrderOwner ? haulerRateBlock || haulRateBlock : null}
              {payRateBlock}
              {driverRateBlock}
            </>
          )}
          {isEditable && ctaToShow === 'add' && <AddRatesCTA />}
        </FlexColumn>
      </RateProvenanceTooltip>
      {isEditableAndHovered && ctaToShow === 'edit' && <EditRatesCTA />}
      {Boolean(ctaToShow) && (
        <Box
          onClick={(event) => event.stopPropagation()}
          data-ui={`page-dispatch__actions__job-${job.jobId}__button`}
        >
          <EditJobRatesDialog
            key={`${showEditRatesDialog ? 'show-edit-rates-dialog' : 'hide-edit-rates-dialog'}-${job.id}`}
            isOrderOwner={isOrderOwner}
            job={showEditRatesDialog ? job : undefined}
            onClose={() => setShowEditRatesDialog(false)}
          />
        </Box>
      )}
    </Column>
  );
}

type InlineJobRateFieldName =
  | `${string}.customerRate`
  | `${string}.vendorRate`
  | `${string}.driverRate`;

interface InlineJobRateFieldProps {
  disabledReason?: string;
  fieldName: InlineJobRateFieldName;
  isDisabled: boolean;
  rateOwnerType: RateOwnerType;
  valueColor: string;
}

function InlineJobRateField({
  disabledReason,
  fieldName,
  isDisabled,
  rateOwnerType,
  valueColor,
}: InlineJobRateFieldProps) {
  const { clearErrors, control, setError, watch } = useFormContext<EditJobTableRowsDto>();

  const watchedRate = watch(fieldName) as InlineJobRateDto;
  const hasValueWithoutType = inlineJobRateHasValueWithoutType(watchedRate);
  const typeFieldName: `${InlineJobRateFieldName}.type` = `${fieldName}.type`;

  // The form-level resolver only reports a root-level "Invalid entry", so the
  // value-requires-a-type rule is surfaced per field here, the same way
  // JobUnitOfMeasureColumn surfaces its conditional requiredness.
  useEffect(() => {
    if (hasValueWithoutType) {
      setError(typeFieldName, { message: t('form_validation_errors.required') });
    } else {
      clearErrors(typeFieldName);
    }

    return () => {
      clearErrors(typeFieldName);
    };
  }, [clearErrors, hasValueWithoutType, setError, typeFieldName]);

  return (
    <RateAndTypeInput
      control={control}
      disabledReason={disabledReason}
      isDisabled={isDisabled}
      name={fieldName}
      rateOwnerType={rateOwnerType}
      sx={{
        [`& .${inputBaseClasses.input}`]: { color: valueColor, fontSize: '12px' },
        [`& .${inputAdornmentClasses.root} .${inputBaseClasses.root}`]: {
          width: INLINE_RATE_TYPE_SELECT_WIDTH,
        },
        [`& .${inputAdornmentClasses.root} .${inputBaseClasses.input}`]: {
          color: 'text.primary',
          fontSize: '12px',
        },
      }}
    />
  );
}

export const JobRatesEditColumn = observer(({ job, order }: JobRatesColumnProps) => {
  const theme = useTheme();
  const { userStore } = useStores();
  const { canEditJob } = userStore.getPermissions();
  const shouldShowHaulerRateCard = useShouldShowHaulerRateCard();
  const canOnlySeeDriverRates = userStore.isDriver;
  const isOrderOwner = userCompanyOwnsOrder(userStore, order);

  const {
    hasCustomerRateSide,
    hasDriverRateSide,
    hasVendorRateSide,
    isAssignedToVendor,
  } = resolveJobRateSides(job);
  const rateEditableSides = resolveRateEditableSides(job, isAssignedToVendor);
  const rateDisabledReasons = resolveRateDisabledReasons(rateEditableSides, isOrderOwner);
  // Bulk edit is gated on order.editable, but a single job can still be
  // non-editable on its own — keep parity with the read-mode column's gate.
  const isJobRateEditingAllowed = job.editable && canEditJob && !userStore.isViewOnlyUser;

  // Hauler mode edits only the hauler rate type, and that stays dialog-only —
  // render it read-only, like the read-mode cell does.
  const haulerRateInfo = shouldShowHaulerRateCard
    ? extractRateInfoFromJob(job, 'hauler')
    : null;

  if (shouldShowHaulerRateCard) {
    return (
      <Column columnKey={JobsTableColumnKey.JOB_RATES}>
        {haulerRateInfo && (
          <OverflowAwareText>
            {t(`rate_types.${snakeCase(haulerRateInfo.rateType)}`)}
          </OverflowAwareText>
        )}
      </Column>
    );
  }

  const shouldRenderCustomerRate = hasCustomerRateSide && !canOnlySeeDriverRates;
  const shouldRenderVendorRate = hasVendorRateSide && !canOnlySeeDriverRates;

  return (
    <Column columnKey={JobsTableColumnKey.JOB_RATES}>
      <Box
        onClick={(event) => event.stopPropagation()}
        data-ui={`page-dispatch__actions__job-${job.jobId}__button`}
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
      >
        {shouldRenderCustomerRate && (
          <InlineJobRateField
            disabledReason={rateDisabledReasons.customer}
            fieldName={`${job.id}.customerRate`}
            isDisabled={
              !isJobRateEditingAllowed || !isOrderOwner || !rateEditableSides.customer
            }
            rateOwnerType={RateOwnerType.CUSTOMER}
            valueColor={theme.brandV2.colors.treadGreen}
          />
        )}

        {shouldRenderVendorRate && (
          <InlineJobRateField
            disabledReason={rateDisabledReasons.assignee}
            fieldName={`${job.id}.vendorRate`}
            isDisabled={!isJobRateEditingAllowed || !rateEditableSides.assignee}
            rateOwnerType={RateOwnerType.VENDOR}
            valueColor={theme.brandV2.colors.treadRed}
          />
        )}

        {hasDriverRateSide && (
          <InlineJobRateField
            disabledReason={rateDisabledReasons.assignee}
            fieldName={`${job.id}.driverRate`}
            isDisabled={!isJobRateEditingAllowed || !rateEditableSides.assignee}
            rateOwnerType={RateOwnerType.DRIVER}
            valueColor={
              canOnlySeeDriverRates
                ? theme.brandV2.colors.treadGreen
                : theme.brandV2.colors.treadRed
            }
          />
        )}
      </Box>
    </Column>
  );
});

export function JobUnitOfMeasureColumn({ job }: JobsTableColumnProps) {
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();
  const {
    clearErrors,
    control,
    formState: { errors, isSubmitting },
    setError,
    watch,
  } = useFormContext<EditJobTableRowsDto>();

  const unitOfMeasureFieldName = `${job.id}.unitOfMeasure`;
  const uom = watch(unitOfMeasureFieldName) as unknown as Nullable<ItemNameAndId>;

  const jobQuantityFieldName = `${job.id}.jobQuantity`;
  const jobQuantity = watch(jobQuantityFieldName) as unknown as Nullable<string | number>;
  const hasEmptyJobQuantity = formQuantityFieldRepresentsEmptyQuantity(jobQuantity);

  const shouldRequireUoM = hasEmptyJobQuantity ? false : !uom?.id;

  useEffect(() => {
    if (shouldRequireUoM) {
      setError(unitOfMeasureFieldName, { message: t('form_validation_errors.required') });
    }

    return () => {
      clearErrors(unitOfMeasureFieldName);
    };
  }, [clearErrors, unitOfMeasureFieldName, shouldRequireUoM, setError, isSubmitting]);

  return (
    <Column columnKey={JobsTableColumnKey.JOB_UNIT_OF_MEASURE}>
      <Box
        onClick={(event) => event.stopPropagation()}
        data-ui={`page-dispatch__actions__job-${job.jobId}__button`}
      >
        <AutocompleteFormField
          control={control}
          errors={errors}
          getLabel={(item) => item.name || ''}
          getValue={(item) => item.id}
          isRequired={shouldRequireUoM}
          list={unitOfMeasureOptions}
          name={unitOfMeasureFieldName}
          sx={{
            fontSize: '12px',
            '& .MuiAutocomplete-inputRoot': { fontSize: '12px', width: '100%' },
            '& .MuiAutocomplete-endAdornment': {
              right: '5px !important',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 'fit-content',
            },
          }}
        />
      </Box>
    </Column>
  );
}

export function JobQuantityColumn({ job }: JobsTableColumnProps) {
  const form = useFormContext<EditJobTableRowsDto>();
  const jobQuantityFieldName = `${job.id}.jobQuantity`;
  const jobUnitOfMeasureFieldName = `${job.id}.unitOfMeasure`;
  const orderQuantityFieldName = `${job.id}.orderQuantity`;
  const orderUnitOfMeasureFieldName = `${job.id}.orderUnitOfMeasure`;

  const jobQuantity = form.watch(jobQuantityFieldName) as unknown as Nullable<
    string | number
  >;
  const jobUom = form.watch(
    jobUnitOfMeasureFieldName,
  ) as unknown as Nullable<ItemNameAndId>;
  const orderQuantity = form.watch(orderQuantityFieldName) as unknown as Nullable<
    string | number
  >;
  const orderUom = form.watch(
    orderUnitOfMeasureFieldName,
  ) as unknown as Nullable<ItemNameAndId>;

  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    // job quantity itself validation
    try {
      jobQuantitySchema.validateSync(jobQuantity);
      setErrorMessage(undefined);
    } catch (error) {
      const e = error as yup.ValidationError;

      setErrorMessage(e.message);

      return;
    }

    // job quantity & uom validation against order qty & uom
    const jobQtyNum = getStringOrNumberFieldPositiveNumberValue(jobQuantity ?? null);
    const orderQtyNum = getStringOrNumberFieldPositiveNumberValue(orderQuantity ?? null);

    if (isNil(jobQtyNum) || isNil(orderQtyNum)) {
      return;
    }

    const jobUomId = jobUom?.id;
    const orderUomId = orderUom?.id;

    if (jobUomId && jobUomId === orderUomId && jobQtyNum > orderQtyNum) {
      setErrorMessage(t('dispatch.validation.job_qty_less_than_order'));
    }
  }, [jobQuantity, orderQuantity, jobUom?.id, orderUom?.id]);

  return (
    <Column columnKey={JobsTableColumnKey.JOB_QUANTITY}>
      <Box
        onClick={(event) => event.stopPropagation()}
        data-ui={`page-dispatch__actions__job-${job.jobId}__button`}
      >
        <NumberInput
          control={form.control}
          slotProps={{ htmlInput: { sx: { fontSize: '12px' } } }}
          isDisabled={form.formState.isSubmitting}
          isRequired
          manualError={errorMessage}
          name={jobQuantityFieldName}
        />
      </Box>
    </Column>
  );
}

interface ActionsColumnProps extends JobsTableColumnProps {
  onCancel: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onEdit: () => void;
  onSplit: () => void;
  onTextDriver: () => void;
  onViewJobCardPdf: () => void;
  isGeneratingJobCardPdf?: boolean;
  isOrderEditable?: boolean;
}

export const ActionsColumn = observer(
  ({
    isHovered,
    job,
    onCancel,
    onDelete,
    onDuplicate,
    onEdit,
    onSplit,
    onTextDriver,
    onViewJobCardPdf,
    isGeneratingJobCardPdf,
    isOrderEditable,
  }: ActionsColumnProps) => {
    const theme = useTheme();
    const { userStore } = useStores();
    const { canCancelJob, canDeleteJob, canEditJob, canTextDrivers } =
      userStore.getPermissions();
    const menuHandler = useRef<MenuHandler>(null);
    const [isStartConversationOpen, setIsStartConversationOpen] = useState(false);

    const isInAppMessagingEnabled = useFeatureFlag({
      featureFlagKey: FeatureFlags.inAppMessaging,
    });
    const { unreadCount: jobUnreadCount } = useMessagingJobUnreadCountQuery(job);

    const isEditable = job.editable && canEditJob;

    const canMessageDriver = canMessageJobDriver(job, isInAppMessagingEnabled);

    const availebleActions = useMemo(() => {
      const actions = [];
      const isCancellable =
        canCancelJob && job.stateEvents.includes(JobStateEvent.CANCEL);
      const isDeletable = isJobDeletable(job);
      const hasPhone = Boolean(job.driver?.phone);

      // View PDF - available to anyone who can see the job
      actions.push({
        icon: <PictureAsPdf sx={{ color: theme.brandV2.colors.treadBlue }} />,
        label: t('dispatch.job_card.view_pdf'),
        onClick: onViewJobCardPdf,
      });

      if (job.copyable) {
        actions.push({
          icon: <FileCopyOutlined sx={{ color: theme.brandV2.colors.treadOrange }} />,
          label: t('dispatch.job.clone_job'),
          onClick: onDuplicate,
        });
      }

      if (job.splittable) {
        actions.push({
          icon: <AccountTree sx={{ color: theme.brandV2.colors.treadOrange }} />,
          label: t('dispatch.job.split_job.title'),
          onClick: onSplit,
        });
      }

      if (hasPhone && canTextDrivers) {
        actions.push({
          icon: <SmsOutlined sx={{ color: theme.brandV2.colors.treadYellowDark }} />,
          label: t('actions.send_text'),
          onClick: onTextDriver,
        });
      }

      if (canMessageDriver) {
        actions.push({
          icon: <ForumOutlined sx={{ color: theme.brandV2.colors.treadYellowDark }} />,
          label: t('messaging.message_driver'),
          onClick: () => setIsStartConversationOpen(true),
        });
      }

      if (isCancellable) {
        actions.push({
          icon: <CancelOutlined sx={{ color: theme.brandV2.colors.treadRed }} />,
          label: t('dispatch.job.cancel_job'),
          onClick: onCancel,
        });
      }

      if (isDeletable) {
        actions.push({
          icon: <DeleteOutlined sx={{ color: theme.brandV2.colors.treadRed }} />,
          label: t('dispatch.job.delete_title'),
          onClick: onDelete,
        });
      }

      return actions;
    }, [
      canCancelJob,
      canDeleteJob,
      canEditJob,
      canMessageDriver,
      isOrderEditable,
      job.driver?.phone,
      job.stateEvents,
      job.copyable,
      job.splittable,
      job.status,
      onViewJobCardPdf,
    ]);

    return (
      <Column columnKey={JobsTableColumnKey.ACTIONS}>
        <FlexColumn sx={{ gap: 1, alignItems: 'flex-end' }}>
          <Box sx={{ minHeight: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX }}>
            {isHovered && isEditable && (
              <SmallButton
                color="brandV2Orange"
                variant="text"
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit();
                }}
                sx={{
                  '&.MuiButtonBase-root': {
                    border: 'solid 1px transparent',
                    '&:hover': { borderColor: theme.brandV2.colors.treadOrange },
                  },
                }}
              >
                {t('actions.edit')}
              </SmallButton>
            )}

            {(isHovered || isGeneratingJobCardPdf) && availebleActions.length > 0 && (
              <Menu
                ref={menuHandler}
                menuTrigger={
                  <SmallButton
                    color="brandV2TreadGray3"
                    variant="text"
                    disabled={isGeneratingJobCardPdf}
                    startIcon={
                      isGeneratingJobCardPdf ? (
                        <Sync
                          sx={{
                            '@keyframes generate-pdf-spinner': {
                              from: { transform: 'rotate(0deg)' },
                              to: { transform: 'rotate(-360deg)' },
                            },
                            animation: 'generate-pdf-spinner 2s linear infinite',
                          }}
                        />
                      ) : (
                        <MoreHoriz />
                      )
                    }
                    sx={{
                      '&.MuiButtonBase-root': {
                        border: 'solid 1px transparent',
                        borderRadius: '50%',
                        height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                        minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                        p: 0,
                        width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                        '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
                        '.MuiButton-startIcon': { mx: 0 },
                      },
                    }}
                  />
                }
              >
                {availebleActions.map((action) => (
                  <MenuItem
                    key={action.label}
                    onClick={(event) => {
                      event.stopPropagation();
                      action.onClick();
                      menuHandler.current?.onClose?.();
                    }}
                    data-ui={`page-dispatch__actions__job-${job.jobId}__button`}
                  >
                    <Box
                      sx={{
                        alignItems: 'center',
                        display: 'flex',
                        width: '100%',
                        gap: 1,
                      }}
                    >
                      {React.cloneElement(action.icon, {
                        sx: { ...action.icon.props.sx, fontSize: '16px' },
                      })}

                      <Typography
                        color={theme.brandV2.colors.treadBlack}
                        sx={{
                          fontSize: '12px',
                        }}
                      >
                        {action.label}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            )}
          </Box>

          <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
            <UnreadCountBadge count={jobUnreadCount} />
            <CopyToClipboard
              textToCopy={job.jobId}
              hideTextWhileCopying
              alertMessageDuration={1000}
              sx={{
                maxHeight: '10px',
              }}
            >
              <Typography
                sx={{ color: theme.brandV2.colors.treadGray3, fontSize: '10px' }}
              >
                {job.jobId}
              </Typography>
            </CopyToClipboard>
          </Box>
        </FlexColumn>

        {/* Outside the hover block: the dialog must outlive the pointer leaving. */}
        {isStartConversationOpen && (
          <StartConversationDialog
            channelName={job.jobId}
            channelType={MessagingChannelType.JOB}
            isOpen
            onClose={() => setIsStartConversationOpen(false)}
            targetId={job.id}
          />
        )}
      </Column>
    );
  },
);

interface JobNotesColumnProps extends JobsTableColumnProps {
  isEditingJobsTableRows?: boolean;
}

export function JobNotesColumn({ isEditingJobsTableRows, job }: JobNotesColumnProps) {
  const form = useFormContext<EditJobTableRowsDto>();
  const jobNotesFieldName = `${job.id}.jobNotes`;

  if (isEditingJobsTableRows) {
    return (
      <Column columnKey={JobsTableColumnKey.NOTES}>
        <FlexColumn>
          <TextInput
            control={form.control}
            isRequired
            maxRows={3}
            name={jobNotesFieldName}
            sx={{ [`& .${inputBaseClasses.input}`]: { fontSize: '12px' } }}
          />
        </FlexColumn>
      </Column>
    );
  }

  return (
    <Column columnKey={JobsTableColumnKey.NOTES}>
      {job.jobNotes && (
        <Tooltip
          title={job.jobNotes}
          slotProps={{
            tooltip: {
              sx: { [`&.${tooltipClasses.tooltip}`]: { whiteSpace: 'pre-line' } },
            },
          }}
        >
          <OverflowAwareText>{job.jobNotes}</OverflowAwareText>
        </Tooltip>
      )}
    </Column>
  );
}
